import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { User } from '@app/models/user.model';
import { ApiResponse } from '@app/models/api-response.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private baseUrl: string = environment.apiBaseUrl + 'admin/';

  constructor(
    private http: HttpClient, 
    private router: Router
  ) {}

  login(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}login`, data);
  }

  logout(): void {
    this.http.post(`${this.baseUrl}logout`, {}).subscribe({
      next: () => {
        this.clearSession();
      },
      error: (err) => {
        console.error('Logout failed:', err);
      },
    });
  }

  private clearSession(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('current_user');
    localStorage.removeItem('last_url');
    localStorage.removeItem('dashboard_selected_tab_index');

    this.router.navigate(['/']);
  }

  setToken(token: string): void {
    localStorage.setItem('auth_token', token);
    // Set a login event timestamp
    localStorage.setItem('login_event', Date.now().toString());
  }

  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  navigateToDefaultUrl(): void {
    const lastUrl = localStorage.getItem('last_url');

    if (!lastUrl || lastUrl === '/' || lastUrl.startsWith('/login')) {
      this.router.navigate(['/dashboard']);
      return;
    }

    this.router.navigateByUrl(lastUrl).catch(() => {
      localStorage.removeItem('last_url');
      this.router.navigate(['/dashboard']);
    });
  }

  checkUserExistsByEmail(email: string): Observable<any> {
    return this.http.get(`${this.baseUrl}user-exists-by-email?email=${encodeURIComponent(email)}`);
  }

  changePassword(data: any): Observable<any> {
    return this.http.put(`${this.baseUrl}reset-password`, data, { headers: { skipAuth: 'true' } });
  }

  setUserToStorage(user: User): void {
    localStorage.setItem('current_user', JSON.stringify(user));
  }

  getUserFromStorage(): User | null {
    const user = JSON.parse(localStorage.getItem('current_user') || '{}');
    return user.id ? user : null;
  }

  getUserSettingsFromStorage(): User['settings'] | null {
    const user = this.getUserFromStorage();
    return user?.settings || null;
  }

  updateUserSettingsInStorage(settings: User['settings']): void {
    const user = this.getUserFromStorage();
    if (!user) return;
    user.settings = settings;
    this.setUserToStorage(user);
  }

  getUser(): Observable<any> {
    return this.http.get(`${this.baseUrl}user`);
  }

  updateUser(data: any): Observable<any> {
    const user = this.getUserFromStorage();
    return this.http.put(`${this.baseUrl}user/${user?.id}`, data);
  }

  // For admin
  deleteUser(): Observable<any> {
    const user = this.getUserFromStorage();
    return this.http.delete(`${this.baseUrl}user/${user?.id}`);
  }

  // User self-service updates
  updateUserName(data: any): Observable<any> {
    return this.http.put(`${this.baseUrl}user/update/name`, data);
  }

  updateUserEmail(data: any): Observable<any> {
    return this.http.put(`${this.baseUrl}user/update/email`, data);
  }

  updateUserPassword(data: any): Observable<any> {
    return this.http.put(`${this.baseUrl}user/update/password`, data);
  }

  updateUserPhone(data: any): Observable<any> {
    return this.http.put(`${this.baseUrl}user/update/phone`, data);
  }

  deleteAccount(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}user/delete-account`, data);
  }

  updateUserSettings(data: any): Observable<ApiResponse<any>> {
    return this.http.put<ApiResponse<any>>(`${this.baseUrl}user/settings`, data);
  }

  getUserSettings(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}user/settings`);
  }

}